/* ==========================================================================
   VIP Photobook Gate — relay to EasyLockdown
   Our file. Never edits an easylockdown_*.liquid snippet (they carry
   SnippetRevision stamps and are regenerated by the app).

   Division of labour: EasyLockdown does all verification. We render the
   design, copy the typed value into their hidden input, call their
   checkUnlockPassword(), and mirror their error state into our markup.

   MEASURED BEHAVIOUR THIS IS BUILT AROUND (onelive-flagship, /pages/gallery):
   - The server DOES send the page content plus the password hash in the raw
     HTML. Their script then REMOVES the .easylockdown-content wrapper on
     locked pages. So "wrapper missing" means LOCKED, not unlocked — the
     opposite of the obvious reading.
   - Their password form is server-rendered on every visit, authenticated or
     not, because Liquid cannot read their auth cookie. So the form being
     present does NOT mean the page is locked. We must wait for their script
     to run before deciding, or authenticated fans get a gate flash.
   ========================================================================== */

(function () {
  'use strict';

  var SEL = {
    nativeForm: 'easylockdown-password-form',
    nativeInput: 'easylockdown-password',
    nativeButton: 'easylockdown-password-form-button',
    nativeError: 'easylockdown-password-error',
    nativeContent: '.easylockdown-content'
  };

  // How long to wait for their app-proxy script before deciding without it.
  var DECIDE_TIMEOUT_MS = 3000;
  // After their script is ready, let it finish its own DOM work before we
  // conclude the page is locked.
  var GRACE_MS = 150;
  // Their check updates the DOM synchronously-ish; give it a tick to settle.
  var SETTLE_MS = 250;
  var READY_TIMEOUT_MS = 8000;

  var designMode = window.Shopify && window.Shopify.designMode;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function isElReady() {
    return (
      typeof window.easylockdown === 'object' &&
      window.easylockdown !== null &&
      typeof window.easylockdown.checkUnlockPassword === 'function'
    );
  }

  function isShown(node) {
    return !!node && window.getComputedStyle(node).display !== 'none';
  }

  /* Unlocked means one of:
     - their form has left the document (they strip it once authenticated, and
       their own no-script fallback strips it too), or
     - the content wrapper is still in the document AND visible.
     A wrapper that has been REMOVED is the locked state, so the
     document.contains() check must come before the visibility check. */
  function isUnlocked() {
    if (!document.getElementById(SEL.nativeForm)) return true;

    var content = document.querySelector(SEL.nativeContent);
    if (content && document.contains(content) && isShown(content)) return true;

    return false;
  }

  function dispatch(el, type) {
    var evt;
    try {
      evt = new Event(type, { bubbles: true });
    } catch (e) {
      evt = document.createEvent('Event');
      evt.initEvent(type, true, true);
    }
    el.dispatchEvent(evt);
  }

  function show(gate) {
    gate.removeAttribute('hidden');
    document.documentElement.classList.add('vip-gate-active');
  }

  function destroy(gate) {
    document.documentElement.classList.remove('vip-gate-active');
    if (gate && gate.parentNode) gate.parentNode.removeChild(gate);
  }

  function init() {
    var gate = document.querySelector('[data-vip-gate]');
    if (!gate) return;

    /* The theme editor bypasses EasyLockdown entirely
       ({% unless request.design_mode %}), so there is no form to relay to.
       Show the gate so it can be styled, but make it inert — otherwise
       merchants see a blank page and assume it's broken. */
    if (designMode) {
      show(gate);
      var form = gate.querySelector('[data-vip-form]');
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
        });
      }
      return;
    }

    decide(gate, Date.now() + DECIDE_TIMEOUT_MS);
  }

  /* Poll until we can tell locked from unlocked. Deliberately does NOT show
     the gate until then — a premature reveal flashes a password screen at fans
     who are already authenticated. */
  function decide(gate, deadline) {
    if (isUnlocked()) {
      destroy(gate);
      return;
    }

    if (isElReady()) {
      setTimeout(function () {
        if (isUnlocked()) destroy(gate);
        else activate(gate);
      }, GRACE_MS);
      return;
    }

    if (Date.now() > deadline) {
      /* Their script never arrived. If their form is still standing, gate the
         page anyway; their own fallback would otherwise reveal the content. */
      if (document.getElementById(SEL.nativeForm)) activate(gate);
      else destroy(gate);
      return;
    }

    setTimeout(function () {
      decide(gate, deadline);
    }, 50);
  }

  function activate(gate) {
    var form = gate.querySelector('[data-vip-form]');
    var input = gate.querySelector('[data-vip-input]');
    var button = gate.querySelector('[data-vip-submit]');
    var errorNode = gate.querySelector('[data-vip-error]');
    var nativeForm = document.getElementById(SEL.nativeForm);
    var nativeInput = document.getElementById(SEL.nativeInput);
    var nativeButton = document.getElementById(SEL.nativeButton);
    var nativeError = document.getElementById(SEL.nativeError);

    if (!form || !input || !nativeInput) return;

    show(gate);

    /* Their form is only moved offscreen by our CSS, so it stays in the tab
       order and the a11y tree. Remove it from both. */
    nativeForm.setAttribute('aria-hidden', 'true');
    nativeInput.setAttribute('tabindex', '-1');
    if (nativeButton) nativeButton.setAttribute('tabindex', '-1');

    try {
      input.focus({ preventScroll: true });
    } catch (e) {
      input.focus();
    }

    function showError() {
      gate.classList.add('vip-gate--error');
      if (errorNode) errorNode.removeAttribute('hidden');
      input.select();
    }

    function clearError() {
      gate.classList.remove('vip-gate--error');
      if (errorNode) errorNode.setAttribute('hidden', '');
    }

    input.addEventListener('input', clearError);

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      if (!input.value) {
        showError();
        return;
      }

      clearError();
      button.setAttribute('aria-busy', 'true');

      whenReady(function (ok) {
        button.removeAttribute('aria-busy');

        if (!ok) {
          destroy(gate);
          return;
        }

        nativeInput.value = input.value;
        dispatch(nativeInput, 'input');
        dispatch(nativeInput, 'change');

        window.easylockdown.checkUnlockPassword(nativeInput);

        setTimeout(function () {
          if (isUnlocked()) destroy(gate);
          else if (isShown(nativeError)) showError();
        }, SETTLE_MS);
      });
    });

    watchForUnlock(gate);
  }

  function whenReady(cb) {
    if (isElReady()) return cb(true);

    var done = false;
    function finish(ok) {
      if (done) return;
      done = true;
      cb(ok);
    }

    document.addEventListener(
      'easylockdown_loaded',
      function () {
        finish(true);
      },
      { once: true }
    );

    var started = Date.now();
    var poll = setInterval(function () {
      if (isElReady()) {
        clearInterval(poll);
        finish(true);
      } else if (Date.now() - started > READY_TIMEOUT_MS) {
        clearInterval(poll);
        finish(false);
      }
    }, 60);
  }

  /* Success isn't signalled by a callback. A correct password typically causes
     their script to reload the page (the content was stripped, so it has to be
     re-fetched), but watch structurally in case it reveals in place instead. */
  function watchForUnlock(gate) {
    var observer = new MutationObserver(function () {
      if (isUnlocked()) {
        observer.disconnect();
        destroy(gate);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  ready(init);
})();
